












































































































































import Vue from "vue";
import Component from "vue-class-component";
import {Prop, Watch} from "vue-property-decorator";
import {Getter} from "vuex-class";
import _ from "lodash";
import Http from "front-component/Http";
import Configuration from "front-component/Configuration";
import MarkdownRenderer from "../MarkdownRenderer.vue";
import AiInteractionRepository from "@/repositories/AiInteractionRepository";
import store from "@/store";
import * as Constants from "@/Constants";

interface McpQueryResponse {
    response_text: string;
    response_id: string;
}

interface ChatMessage {
    role: "user" | "assistant";
    content: string;
}

interface UserConversation {
    uuid: string;
    question: string;
}

interface DisplayedPrompt {
    uuid: string | null;
    label: string;
}

interface AiInteractionItem {
    uuid: string;
    question: string;
    response: string | null;
    responseId?: string;
    createdAt: string;
}

interface StreamPayload {
    clientUuid: string;
    question: string;
    previousResponseId?: string;
}

interface StreamEvent {
    type: "status" | "delta" | "done" | "error";
    text?: string;
    response_id?: string;
    message?: string;
    key?: string;
}

@Component({
    components: {
        MarkdownRenderer
    }
})
export default class ChatBotDrawerMcp extends Vue {
    @Prop({default: false}) public visible!: boolean;
    @Getter("getCurrentOwnerUuid") public currentOwnerUuid!: string;

    private promptsCollapsed: boolean = false;
    private chatInput: string = "";
    private isThinking: boolean = false;
    private currentStatus: string = "";
    private messages: ChatMessage[] = [];
    private previousResponseId: string | null = null;
    private predefinedPrompts: string[] = [
        "analysisOperationDecember",
        "analysisCpmCompare",
        "analysisEvolutionCpc",
        "analysisBudgetTotal",
        "analysisBestRateCampaignClick"
    ];
    private userConversations: UserConversation[] = [];
    private userQuestionsLoaded: boolean = false;
    private isLoadingConversation: boolean = false;
    private showToggleButton: boolean = true;
    private showPromptContent: boolean = true;

    private get drawerVisible(): boolean {
        return this.visible;
    }

    private set drawerVisible(value: boolean) {
        this.$emit("update:visible", value);
    }

    private get displayedPrompts(): DisplayedPrompt[] {
        if (!this.userQuestionsLoaded) {
            return [];
        }
        if (this.userConversations.length > 0) {
            return this.userConversations.map((c: UserConversation) => ({uuid: c.uuid, label: c.question}));
        }
        return this.predefinedPrompts.map((p: string) => ({uuid: null, label: p}));
    }

    private get hasUserQuestions(): boolean {
        return this.userConversations.length > 0;
    }

    private get promptsLabel(): string {
        return this.hasUserQuestions ? "myQuestions" : "sentenceAI";
    }

    public created(): void {
        this.loadUserConversations();
    }

    @Watch("currentOwnerUuid")
    private onOwnerChanged(): void {
        this.loadUserConversations();
    }

    private loadUserConversations(silent: boolean = false): void {
        if (!this.currentOwnerUuid) {
            return;
        }

        if (!silent) {
            this.userQuestionsLoaded = false;
        }
        AiInteractionRepository.get({
            fields: ["uuid", "question"],
            filters: {
                type: Constants.AiInteractionTypes.campaignMcp,
                ownerUuid: this.currentOwnerUuid,
                onlyParent: true
            },
            pagination: {
                nb_per_page: Constants.MAX_AI_LAST_QUESTIONS,
                page: 1,
            }
        }).then((data: object[]) => {
            this.userConversations = (data as AiInteractionItem[])
                .map((item: AiInteractionItem) => ({uuid: item.uuid, question: item.question}))
                .filter((c: UserConversation) => !!c.uuid && !!c.question);
            this.userQuestionsLoaded = true;
        }).catch(() => {
            this.userQuestionsLoaded = true;
        });
    }

    private selectPrompt(prompt: DisplayedPrompt): void {
        if (prompt.uuid) {
            this.loadConversation(prompt.uuid);
            return;
        }
        this.chatInput = _.invoke(this.$options.filters, "trans", prompt.label);
    }

    private deleteConversation(uuid: string): void {
        this.$confirm(_.invoke(this.$options.filters, "trans", "deleteConversationConfirmation"))
            .then(() => {
                const index = this.userConversations.findIndex((c: UserConversation) => c.uuid === uuid);
                if (index > -1) {
                    this.userConversations.splice(index, 1);
                }
                this.resetChat();
                AiInteractionRepository.save({uuid, deletedAt: new Date().toISOString()}).catch(() => {
                    this.loadUserConversations(true);
                    this.$message({
                        type: "error",
                        message: _.invoke(this.$options.filters, "trans", "cannotDeleteConversation"),
                    });
                });
            }).catch(() => {
                return;
            });
    }

    private parseResponse(raw: string | null): McpQueryResponse | null {
        if (!raw) {
            return null;
        }
        try {
            return JSON.parse(raw) as McpQueryResponse;
        } catch (e) {
            return null;
        }
    }

    private loadConversation(uuid: string): void {
        this.resetChat();
        this.isLoadingConversation = true;
        AiInteractionRepository.get({
            fields: ["question", "response", "responseId", "createdAt"],
            filters: {
                type: Constants.AiInteractionTypes.campaignMcp,
                ownerUuid: this.currentOwnerUuid,
                parentUuid: uuid
            }
        }).then((data: object[]) => {
            const sorted = _.sortBy(data as AiInteractionItem[], (item: AiInteractionItem) => item.createdAt);
            const messages: ChatMessage[] = [];
            let lastResponseId: string | null = null;
            sorted.forEach((item: AiInteractionItem) => {
                messages.push({role: "user", content: item.question});
                const parsed = this.parseResponse(item.response);
                if (parsed) {
                    messages.push({
                        role: "assistant",
                        content: parsed.response_text,
                    });
                    lastResponseId = parsed.response_id || item.responseId || lastResponseId;
                } else {
                    messages.push({
                        role: "assistant",
                        content: _.invoke(this.$options.filters, "trans", "chatBotError"),
                    });
                }
            });
            this.messages = messages;
            this.previousResponseId = lastResponseId;
            this.isLoadingConversation = false;
            this.scrollToBottom();
        }).catch(() => {
            this.isLoadingConversation = false;
        });
    }

    private scrollToBottom(): void {
        this.$nextTick(() => {
            const container = this.$refs.messagesContainer as HTMLDivElement;
            if (container) {
                container.scrollTop = container.scrollHeight;
            }
        });
    }

    private async sendMessage(): Promise<void> {
        const message = this.chatInput.trim();
        if (!message || this.isThinking) {
            return;
        }

        this.messages.push({role: "user", content: message});
        this.chatInput = "";
        this.isThinking = true;
        this.currentStatus = this.translateStatus();
        this.scrollToBottom();

        const isNewConversation = !this.previousResponseId;

        const payload: StreamPayload = {
            clientUuid: this.currentOwnerUuid,
            question: message,
        };
        if (this.previousResponseId) {
            payload.previousResponseId = this.previousResponseId;
        }

        const assistantMsg: ChatMessage = {role: "assistant", content: ""};
        let assistantPushed: boolean = false;
        let responseId: string | null = null;

        try {
            const headers: Record<string, string> = {
                ...(Http.getBaseObject().getHeaders() as Record<string, string>),
                "Content-Type": "application/json",
            };

            const res: Response = await fetch(
                Configuration.get("apiUri") + "/ai/campaign/mcp_query/stream",
                {
                    method: "POST",
                    headers,
                    body: JSON.stringify(payload),
                }
            );

            if (!res.ok || !res.body) {
                throw new Error("stream error");
            }

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";

            while (true) {
                const {done, value} = await reader.read();
                if (done) {
                    break;
                }
                buffer += decoder.decode(value, {stream: true});

                let sepIndex: number = buffer.indexOf("\n\n");
                while (sepIndex !== -1) {
                    const rawEvent = buffer.slice(0, sepIndex);
                    buffer = buffer.slice(sepIndex + 2);
                    sepIndex = buffer.indexOf("\n\n");
                    if (!rawEvent.startsWith("data:")) {
                        continue;
                    }
                    let evt: StreamEvent;
                    try {
                        evt = JSON.parse(rawEvent.slice(5).trim()) as StreamEvent;
                    } catch {
                        continue;
                    }

                    if (evt.type === "status") {
                        this.currentStatus = this.translateStatus(evt.key);
                        this.scrollToBottom();
                    } else if (evt.type === "delta") {
                        if (!assistantPushed) {
                            this.isThinking = false;
                            this.currentStatus = "";
                            this.messages.push(assistantMsg);
                            assistantPushed = true;
                        }
                        assistantMsg.content += evt.text;
                        this.scrollToBottom();
                    } else if (evt.type === "done") {
                        responseId = evt.response_id || null;
                    } else if (evt.type === "error") {
                        throw new Error(evt.message);
                    }
                }
            }

            this.isThinking = false;
            this.currentStatus = "";

            if (!assistantMsg.content.trim()) {
                if (!assistantPushed) {
                    this.messages.push(assistantMsg);
                }
                assistantMsg.content = _.invoke(this.$options.filters, "trans", "chatBotError");
            }

            this.previousResponseId = responseId;
            if (isNewConversation && responseId) {
                this.loadUserConversations(true);
            }
        } catch {
            this.isThinking = false;
            this.currentStatus = "";
            if (!assistantPushed) {
                this.messages.push(assistantMsg);
            }
            assistantMsg.content = _.invoke(this.$options.filters, "trans", "chatBotError");
        }
    }

    private translateStatus(key?: string): string {
        const labelKey = (key && Constants.mcpStatusLabelKeys[key]) || "mcpStatusProcessing";
        return _.invoke(this.$options.filters, "trans", labelKey);
    }

    private resetChat(): void {
        this.messages = [];
        this.previousResponseId = null;
        this.chatInput = "";
        this.isThinking = false;
        this.currentStatus = "";
    }

    private togglePrompts(): void {
        this.showToggleButton = false;
        if (!this.promptsCollapsed) {
            this.showPromptContent = false;
            this.promptsCollapsed = true;
        } else {
            this.promptsCollapsed = false;
        }
        setTimeout(() => {
            this.showPromptContent = !this.promptsCollapsed;
            this.showToggleButton = true;
        }, 350);
    }
}
